import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { auth as AuthGen } from '@valvur/shared-grpc';
import { DialogService } from '../../../../core/services/dialog.service';
import { PARENT_DIALOG_PANEL_CLASS } from '../../../../shared/constants/parent-dialog.constants';
import { ParentButtonComponent } from '../../../../shared/components/ui/parent-button/parent-button.component';
import { InitialsPipe } from '../../../../shared/pipes/initials.pipe';
import { PluralizationPipe } from '../../../../shared/pipes/pluralization.pipe';
import { IdentityNavigationService } from '../../../identity/services/identity-navigation.service';
import { ParentIdentityService } from '../../../identity/services/parent-identity.service';
import { LinkingWizardComponent } from '../../../user/components/linking-wizard/linking-wizard.component';
import { UserService } from '../../../user/service/user.service';
import { CoParentInviteDialogComponent } from '../../components/co-parent-invite-dialog/co-parent-invite-dialog.component';
import { FamilyHubService } from '../../services/family-hub.service';
import { FamilyKidsService } from '../../services/family-kids.service';
import { Router } from '@angular/router';
import {
  hasKidsMaxSeatAvailable,
  kidsMaxLimit,
  showKidsMaxUpgrade,
} from '../../../user/utils/plan-kids-limit.util';

const EXTENDED_INVITE_SCOPE = 'family.extended_invite';
const EXTENDED_SEATS_SCOPE = 'family.extended_seats';

const PARENTAL_FIGURE_OPTIONS = [
  'MOTHER',
  'FATHER',
  'GRANDMOTHER',
  'GRANDFATHER',
  'UNCLE',
  'AUNT',
  'OTHER',
] as const;

const CHILDREN_PREVIEW_LIMIT = 3;

@Component({
  selector: 'app-family-hub',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslatePipe,
    ParentButtonComponent,
    InitialsPipe,
    PluralizationPipe,
  ],
  templateUrl: './family-hub.component.html',
  styleUrl: './family-hub.component.scss',
})
export class FamilyHubComponent {
  #familyHub = inject(FamilyHubService);
  #familyKids = inject(FamilyKidsService);
  #userService = inject(UserService);
  #dialog = inject(DialogService);
  #identity = inject(ParentIdentityService);
  #identityNav = inject(IdentityNavigationService);
  #translate = inject(TranslateService);
  #router = inject(Router);

  readonly figureOptions = PARENTAL_FIGURE_OPTIONS;
  readonly childrenPreviewLimit = CHILDREN_PREVIEW_LIMIT;
  softPromptDismissed = signal(false);
  showAllChildren = signal(false);

  family = computed(() => this.#familyHub.familyQuery.data());
  invites = computed(() => this.#familyHub.invitesQuery.data());
  isLoading = computed(
    () =>
      this.#familyHub.familyQuery.isLoading() ||
      this.#familyHub.invitesQuery.isLoading(),
  );
  currentUserId = computed(() => this.#userService.user()?.id ?? '');

  myMembership = computed(() => {
    const uid = this.currentUserId();
    return this.family()?.members?.find((m) => m.userId === uid);
  });

  myParentalFigure = computed(
    () => this.myMembership()?.parentalFigure ?? '',
  );

  showSoftPrompt = computed(
    () =>
      !this.softPromptDismissed() &&
      !this.myParentalFigure() &&
      !!this.family(),
  );

  /** Co-parent invite only when seat is free (max 1 CO_PARENT). */
  hasOpenCoParentSeat = computed(
    () => !this.adults().some((m) => m.role === 'CO_PARENT'),
  );

  canInvite = computed(() => {
    const role = this.myMembership()?.role;
    if (role !== 'PRIMARY' && role !== 'CO_PARENT') {
      return false;
    }
    return this.hasOpenCoParentSeat();
  });

  extendedMemberCount = computed(
    () => this.adults().filter((m) => m.role === 'EXTENDED').length,
  );

  /**
   * PRIMARY only.
   * When payments/quotas are off: invite is allowed (matches backend).
   * When quotas are on: requires open `family.extended_seats`.
   */
  canInviteExtended = computed(() => {
    if (this.myMembership()?.role !== 'PRIMARY') {
      return false;
    }
    if (!this.#userService.quotasEnforced()) {
      return true;
    }
    const scopes = this.#userService.entitlement()?.scopes;
    if (!scopes) {
      return false;
    }
    const inviteCap = scopes[EXTENDED_INVITE_SCOPE];
    if (inviteCap === undefined || inviteCap === 0) {
      return false;
    }
    const seatLimit = scopes[EXTENDED_SEATS_SCOPE];
    if (seatLimit === undefined || seatLimit === 0) {
      return false;
    }
    if (seatLimit < 0) {
      return true;
    }
    return this.extendedMemberCount() < seatLimit;
  });

  /** Upgrade nudge only when payments UX is live and plan has no caregiver seats. */
  showExtendedUpgrade = computed(() => {
    if (!this.#userService.paymentsEnabled()) {
      return false;
    }
    if (this.myMembership()?.role !== 'PRIMARY') {
      return false;
    }
    if (this.canInviteExtended()) {
      return false;
    }
    const scopes = this.#userService.entitlement()?.scopes;
    if (!scopes) {
      return false;
    }
    const seatLimit = scopes[EXTENDED_SEATS_SCOPE] ?? 0;
    const inviteCap = scopes[EXTENDED_INVITE_SCOPE] ?? 0;
    return inviteCap === 0 || seatLimit === 0;
  });

  canAddChildByRole = computed(() => {
    const role = this.myMembership()?.role;
    return role === 'PRIMARY' || role === 'CO_PARENT';
  });

  kidsMaxPlanContext = computed(() => ({
    quotasEnforced: this.#userService.quotasEnforced(),
    paymentsEnabled: this.#userService.paymentsEnabled(),
    scopes: this.#userService.entitlement()?.scopes,
    activeChildCount: this.#familyKids.activeChildCount(),
  }));

  kidsMaxSeatLimit = computed(() =>
    kidsMaxLimit(this.kidsMaxPlanContext().scopes),
  );

  canAddChild = computed(() => {
    if (!this.canAddChildByRole()) {
      return false;
    }
    return hasKidsMaxSeatAvailable(this.kidsMaxPlanContext());
  });

  showKidsUpgrade = computed(() =>
    showKidsMaxUpgrade({
      ...this.kidsMaxPlanContext(),
      canAddByRole: this.canAddChildByRole(),
    }),
  );

  canRemoveCoParents = computed(
    () => this.myMembership()?.role === 'PRIMARY',
  );

  canLeaveFamily = computed(
    () => this.myMembership()?.role === 'CO_PARENT',
  );

  adults = computed(
    () =>
      this.family()?.members?.filter(
        (m) =>
          m.role === 'PRIMARY' ||
          m.role === 'CO_PARENT' ||
          m.role === 'EXTENDED',
      ) ?? [],
  );

  children = this.#familyKids.children;

  visibleChildren = computed(() => {
    const kids = this.children();
    if (this.showAllChildren() || kids.length <= CHILDREN_PREVIEW_LIMIT) {
      return kids;
    }
    return kids.slice(0, CHILDREN_PREVIEW_LIMIT);
  });

  incoming = computed(() => this.invites()?.incoming ?? []);
  outgoing = computed(() => this.invites()?.outgoing ?? []);

  /** Solo PRIMARY may have signed up via invite with a lost return link. */
  showInviteRecoveryHint = computed(() => {
    if (this.incoming().length > 0) {
      return false;
    }
    if (this.myMembership()?.role !== 'PRIMARY') {
      return false;
    }
    if (this.adults().length !== 1) {
      return false;
    }
    return !this.#familyKids.hasChildren();
  });

  async onFigureChange(value: string): Promise<void> {
    if (!value) return;
    await this.#familyHub.updateParentalFigure.mutateAsync(value);
  }

  dismissSoftPrompt(): void {
    this.softPromptDismissed.set(true);
  }

  toggleShowAllChildren(): void {
    this.showAllChildren.update((open) => !open);
  }

  async openInvite(): Promise<void> {
    if (!this.canInvite()) return;
    await this.#identity.whenConfigReady();
    if (this.#identity.layerEnabled() && !this.#identity.canExpandFamily()) {
      void this.#identityNav.navigateToVerification();
      return;
    }
    this.#dialog.open(
      CoParentInviteDialogComponent,
      { kind: 'co-parent' },
      { panelClass: PARENT_DIALOG_PANEL_CLASS },
    );
  }

  openInviteAnyway(): void {
    this.softPromptDismissed.set(true);
    void this.openInvite();
  }

  async openInviteExtended(): Promise<void> {
    if (!this.canInviteExtended()) return;
    await this.#identity.whenConfigReady();
    if (this.#identity.layerEnabled() && !this.#identity.canExpandFamily()) {
      void this.#identityNav.navigateToVerification();
      return;
    }
    this.#dialog.open(
      CoParentInviteDialogComponent,
      { kind: 'caregiver' },
      { panelClass: PARENT_DIALOG_PANEL_CLASS },
    );
  }

  goExtendedUpgrade(): void {
    this.goSubscriptions();
  }

  goKidsUpgrade(): void {
    this.goSubscriptions();
  }

  private goSubscriptions(): void {
    void this.#router.navigate(['/profile'], {
      queryParams: { segment: 'subscriptions' },
    });
  }

  async openAddKidDialog(): Promise<void> {
    if (!this.canAddChild()) return;
    await this.#identity.whenConfigReady();
    if (this.#identity.layerEnabled() && !this.#identity.canExpandFamily()) {
      void this.#identityNav.navigateToVerification();
      return;
    }

    this.#dialog
      .open(
        LinkingWizardComponent,
        { user: this.#userService.user() },
        { panelClass: PARENT_DIALOG_PANEL_CLASS },
      )
      .subscribe(() => {
        void this.#familyKids.refetch();
      });
  }

  async acceptIncoming(invite: AuthGen.CoParentInvite): Promise<void> {
    await this.#familyHub.acceptInvite.mutateAsync({ inviteId: invite.id });
    await this.#identityNav.navigateToVerification();
  }

  async rejectIncoming(invite: AuthGen.CoParentInvite): Promise<void> {
    await this.#familyHub.rejectInvite.mutateAsync({ inviteId: invite.id });
  }

  async removeCoParent(member: AuthGen.FamilyMember): Promise<void> {
    const ok = confirm(
      this.#translate.instant('app.familyHub.removeCoParentConfirm'),
    );
    if (!ok) {
      return;
    }
    await this.#familyHub.removeCoParent.mutateAsync(member.userId);
  }

  async leaveFamily(): Promise<void> {
    const ok = confirm(
      this.#translate.instant('app.familyHub.leaveFamilyConfirm'),
    );
    if (!ok) {
      return;
    }
    await this.#familyHub.leaveFamily.mutateAsync();
  }

  childDisplayName(member: AuthGen.FamilyMember): string {
    return this.#familyKids.displayName(member);
  }

  getDutiesCount(member: AuthGen.FamilyMember): number {
    return member.pendingDutyCount ?? 0;
  }

  getTasksCount(member: AuthGen.FamilyMember): number {
    return member.incompleteTaskCount ?? 0;
  }

  figureLabelKey(figure: string): string {
    const key = (figure || '').toLowerCase();
    return key
      ? `app.familyHub.parentalFigure.${key}`
      : 'app.familyHub.parentalFigure.unset';
  }

  roleLabelKey(role: string): string {
    return `app.familyHub.roles.${(role || '').toLowerCase()}`;
  }

  roleBadgeClass(role: string): string {
    switch (role) {
      case 'PRIMARY':
        return 'family-hub__badge--primary';
      case 'CO_PARENT':
        return 'family-hub__badge--coparent';
      case 'EXTENDED':
        return 'family-hub__badge--extended';
      case 'CHILD':
        return 'family-hub__badge--child';
      default:
        return '';
    }
  }

  memberInitials(name: string | undefined, fallbackId: string): string {
    const source = (name || fallbackId || '?').trim();
    const parts = source.split(/[\s._-]+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return source.slice(0, 2).toUpperCase();
  }

  isMe(userId: string): boolean {
    return userId === this.currentUserId();
  }
}
